import { useEffect, useRef, useState } from 'react';
import { useAIAssistant } from '@/contexts/ai-assistant-context';
import { AIAssistantMessage } from './ai-assistant-message';
import { Loader2, Sparkles } from 'lucide-react';

const THINKING_MESSAGES = [
  'AI is thinking...',
  'Processing...',
  'Working on it...',
];

export function AIAssistantChat() {
  const { messages, isProcessing } = useAIAssistant();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [thinkingMessage, setThinkingMessage] = useState(THINKING_MESSAGES[0]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Rotate thinking messages while processing
  useEffect(() => {
    if (!isProcessing) return;

    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % THINKING_MESSAGES.length;
      setThinkingMessage(THINKING_MESSAGES[index]);
    }, 2000);

    return () => clearInterval(interval);
  }, [isProcessing]);

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Loader2 className="h-8 w-8 text-primary animate-pulse" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Start a conversation</h3>
        <p className="text-sm text-muted-foreground max-w-[280px]">
          Ask me anything about your assets, or try one of the suggestions above.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {messages.map((message) => (
        <AIAssistantMessage key={message.id} message={message} />
      ))}
      
      {isProcessing && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50 border border-border/50">
          <div className="relative">
            <Sparkles className="h-5 w-5 text-primary animate-pulse" />
            <Loader2 className="h-3 w-3 text-primary animate-spin absolute -bottom-1 -right-1" />
          </div>
          <span className="text-sm text-muted-foreground font-medium">{thinkingMessage}</span>
        </div>
      )}
      
      <div ref={messagesEndRef} />
    </div>
  );
}
